import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import DetailsTable from 'src/components/DetailsTable';
import Iconify from 'src/components/Iconify';
import SuccessButton from 'src/components/SuccessButton';
import TableActionButton from 'src/components/TableActionButton';
import DeleteDeploymentRingDialog from 'src/features/deploymentRing/DeleteDeploymentRingDialog';
import EditDeploymentRingModal from 'src/features/deploymentRing/EditDeploymentRingModal';
import { useActivateDeploymentRing, useSetDefaultDeploymentRing } from 'src/hooks/deploymentRing';
import { pagesText } from 'src/constants/pagesText';

const toRows = ring => [
  { key: 'name', label: 'Name', value: ring.name },
  { key: 'status', label: 'Status', value: ring.isActive ? 'Active' : 'Inactive' },
  { key: 'deferralDays', label: 'Deferral days', value: ring.deferralDays },
  { key: 'isDefault', label: 'Is Default ring', value: ring.isDefault ? 'Yes' : 'No' },
];

const DeploymentRingDetailsGeneral = () => {
  const { ring } = useOutletContext();
  const { enqueueSnackbar } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { mutateAsync: activateDeploymentRing, isLoading: isActivating } =
    useActivateDeploymentRing();
  const { mutateAsync: setDefaultDeploymentRing, isLoading: isSettingDefault } =
    useSetDefaultDeploymentRing();

  const runAction = async (action, successMessage) => {
    try {
      await action({ ringId: ring.id });
      enqueueSnackbar(successMessage, { variant: 'success', autoHideDuration: 3000 });
    } catch (error) {
      enqueueSnackbar(typeof error === 'string' && error ? error : pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 4000,
      });
    }
  };

  const handleActivate = () => runAction(activateDeploymentRing, `${ring.name} is active again.`);

  const handleSetDefault = () =>
    runAction(setDefaultDeploymentRing, `${ring.name} is now the default ring.`);

  return (
    <Stack gap={2} sx={{ mt: 2 }}>
      <Stack direction="row" gap={1} justifyContent="flex-end">
        {ring.isActive ? (
          <>
            {!ring.isDefault ? (
              <TableActionButton
                variant="outlined"
                disabled={isSettingDefault}
                onClick={handleSetDefault}
                startIcon={<Iconify icon="mdi:star-outline" width={16} height={16} />}
                sx={{ px: 1.5 }}>
                Set as Default
              </TableActionButton>
            ) : null}
            <TableActionButton
              variant="outlined"
              onClick={() => setEditing(true)}
              startIcon={<Iconify icon="ic:baseline-edit" width={16} height={16} />}
              sx={{ px: 1.5 }}>
              Edit
            </TableActionButton>
            <TableActionButton
              variant="outlined"
              disabled={ring.isDefault}
              onClick={() => setDeleting(true)}
              startIcon={<Iconify icon="mdi:trash-can-outline" width={16} height={16} />}
              sx={{ px: 1.5 }}>
              Deactivate
            </TableActionButton>
          </>
        ) : (
          <SuccessButton size="small" disabled={isActivating} onClick={handleActivate}>
            Activate
          </SuccessButton>
        )}
      </Stack>

      <DetailsTable rows={toRows(ring)} />

      {editing ? (
        <EditDeploymentRingModal
          ring={ring}
          isDefaultRing={ring.isDefault}
          onClose={() => setEditing(false)}
        />
      ) : null}

      {deleting ? (
        <DeleteDeploymentRingDialog
          ring={ring}
          onCancel={() => setDeleting(false)}
          onDeleted={() => setDeleting(false)}
        />
      ) : null}
    </Stack>
  );
};

export default DeploymentRingDetailsGeneral;
